import { AxiosError } from 'axios';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import initApi from '@/api/initApiV2';

import {
  PoliciesApi,
  type Comment,
  type Log,
  type PoliciesApiAcknowledgePolicyRequest,
  type PoliciesApiCreatePolicyCommentsRequest,
  type PoliciesApiCreatePolicyRequest,
  type PoliciesApiCreatePolicyVersionCommentsRequest,
  type PoliciesApiCreatePolicyVersionRequest,
  type PoliciesApiDeletePolicyVersionContentFileRequest,
  type PoliciesApiExportPolicyVersionPdfRequest,
  type PoliciesApiGetAcknowledgmentProgressPerPolicyRequest,
  type PoliciesApiGetHistoricalAcknowledgmentsRequest,
  type PoliciesApiGetPolicyCommentsRequest,
  type PoliciesApiGetPolicyContentFileRequest,
  type PoliciesApiGetPolicyLogsRequest,
  type PoliciesApiGetPolicyRequest,
  type PoliciesApiGetPolicyVersionCommentsRequest,
  type PoliciesApiGetPolicyVersionContentFileRequest,
  type PoliciesApiGetPolicyVersionLogsRequest,
  type PoliciesApiGetPolicyVersionRequest,
  type PoliciesApiGetTemplateUsedByPolicyRequest,
  type PoliciesApiGetUserAcknowledgmentProgressRequest,
  type PoliciesApiQueryAcknowledgeablePoliciesRequest,
  type PoliciesApiQueryPoliciesExpandedRequest,
  type PoliciesApiQueryPoliciesRequest,
  type PoliciesApiQueryPolicyAcknowledgmentsExpandedRequest,
  type PoliciesApiQueryPolicyVersionsRequest,
  type PoliciesApiUpdatePolicyOwnerRequest,
  type PoliciesApiUpdatePolicyStatusRequest,
  type PoliciesApiUpdatePolicyVersionRequest,
  type PoliciesApiUpdatePolicyVersionStatusRequest,
  type PoliciesApiUploadPolicyVersionFileRequest,
  type Policy,
  type PolicyAcknowledgmentExpandedPaginated,
  type PolicyAcknowledgmentOverview,
  type PolicyAcknowledgmentPaginated,
  type PolicyAcknowledgmentProgressPaginated,
  type PolicyOverviewPaginated,
  type PolicyPaginated,
  type PolicyTemplate,
  type PolicyVersion,
  type PolicyVersionPaginated,
  type UserAcknowledgmentProgressPaginated,
} from '@/clients/v2';

import { handleApiError } from '@/api/handleApiError.ts';
import type { RequestConfig } from '@/api/Request';
import { downloadFile } from '@/utils/files.ts';

export const usePoliciesStore = defineStore('policies', () => {
  const policiesData = ref<PolicyPaginated | null>(null);
  const policiesExpandedData = ref<PolicyOverviewPaginated | null>(null);
  const currentPolicy = ref<Policy | null>(null);
  const currentPolicyVersion = ref<PolicyVersion | null>(null);
  const policyVersionsData = ref<PolicyVersionPaginated | null>(null);
  const policyLogs = ref<Log[]>([]);
  const policyComments = ref<Comment[]>([]);
  const policyVersionLogs = ref<Log[]>([]);
  const policyVersionComments = ref<Comment[]>([]);
  const policiesAcknowledgementsData = ref<PolicyAcknowledgmentPaginated | null>(null);
  const policiesAcknowledgementsExpandedData = ref<PolicyAcknowledgmentExpandedPaginated | null>(
    null,
  );
  const acknowledgementsOverviewData = ref<PolicyAcknowledgmentOverview | null>(null);
  const acknowledgementsPerPolicyData = ref<PolicyAcknowledgmentProgressPaginated | null>(null);
  const acknowledgementsPerUserData = ref<UserAcknowledgmentProgressPaginated | null>(null);
  const userAcknowledgementHistoryData = ref<PolicyAcknowledgmentExpandedPaginated | null>(null);
  const sourceTemplate = ref<PolicyTemplate | null>(null);

  const queryPolicies = async (
    params: PoliciesApiQueryPoliciesRequest = {},
    options?: RequestConfig,
  ) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.queryPolicies(params, options);

      if (!response) return;

      policiesData.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const queryPoliciesExpanded = async (
    params: PoliciesApiQueryPoliciesExpandedRequest = {},
    options?: RequestConfig,
  ) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.queryPoliciesExpanded(params, options);

      if (!response) return;

      policiesExpandedData.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getPolicy = async (params: PoliciesApiGetPolicyRequest) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.getPolicy(params);

      if (!response) return;

      currentPolicy.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const createPolicy = async (params: PoliciesApiCreatePolicyRequest) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.createPolicy(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updatePolicyOwner = async (params: PoliciesApiUpdatePolicyOwnerRequest) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.updatePolicyOwner(params);

      if (!response) return;

      currentPolicy.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const createPolicyVersion = async (params: PoliciesApiCreatePolicyVersionRequest) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.createPolicyVersion(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getPolicyVersion = async (params: PoliciesApiGetPolicyVersionRequest) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.getPolicyVersion(params);

      if (!response) return;

      currentPolicyVersion.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const queryPolicyVersions = async (
    params: PoliciesApiQueryPolicyVersionsRequest,
    options?: RequestConfig,
  ) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.queryPolicyVersions(params, options);

      if (!response) return;

      policyVersionsData.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updatePolicyVersion = async (params: PoliciesApiUpdatePolicyVersionRequest) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.updatePolicyVersion(params);

      if (!response) return;

      currentPolicyVersion.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updatePolicyVersionStatus = async (params: PoliciesApiUpdatePolicyVersionStatusRequest) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.updatePolicyVersionStatus(params);

      if (!response) return;

      currentPolicyVersion.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getPolicyLogs = async (params: PoliciesApiGetPolicyLogsRequest) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.getPolicyLogs(params);

      if (!response) return;

      policyLogs.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getPolicyComments = async (params: PoliciesApiGetPolicyCommentsRequest) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.getPolicyComments(params);

      if (!response) return;

      policyComments.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const postPolicyComment = async (params: PoliciesApiCreatePolicyCommentsRequest) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.createPolicyComments(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getPolicyVersionComments = async (params: PoliciesApiGetPolicyVersionCommentsRequest) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.getPolicyVersionComments(params);

      if (!response) return;

      policyVersionComments.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const postPolicyVersionComment = async (
    params: PoliciesApiCreatePolicyVersionCommentsRequest,
  ) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.createPolicyVersionComments(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getPolicyVersionLogs = async (params: PoliciesApiGetPolicyVersionLogsRequest) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.getPolicyVersionLogs(params);

      if (!response) return;

      policyVersionLogs.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const updatePolicyStatus = async (params: PoliciesApiUpdatePolicyStatusRequest) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const policy = await policiesApi?.updatePolicyStatus(params);

      if (!policy) return;

      currentPolicy.value = policy.data;

      return policy.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const acknowledgePolicy = async (params: PoliciesApiAcknowledgePolicyRequest) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.acknowledgePolicy(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const queryPoliciesAcknowledgements = async (
    params: PoliciesApiQueryAcknowledgeablePoliciesRequest = {},
    options?: RequestConfig,
  ) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.queryAcknowledgeablePolicies(params, options);

      if (!response) return;

      policiesAcknowledgementsData.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const queryPoliciesAcknowledgementsExpanded = async (
    params: PoliciesApiQueryPolicyAcknowledgmentsExpandedRequest,
    options?: RequestConfig,
    reassign: boolean = true,
  ) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.queryPolicyAcknowledgmentsExpanded(params, options);

      if (!response) return;

      if (reassign) {
        policiesAcknowledgementsExpandedData.value = response.data;
      }

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const queryUserAcknowledgementHistory = async (
    params: PoliciesApiGetHistoricalAcknowledgmentsRequest,
    options?: RequestConfig,
  ) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.getHistoricalAcknowledgments(params, options);

      if (!response) return;

      userAcknowledgementHistoryData.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getAcknowledgementsOverview = async () => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.getPolicyAcknowledgmentsOverview();

      if (!response) return;

      acknowledgementsOverviewData.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getAcknowledgementsPerPolicy = async (
    params: PoliciesApiGetAcknowledgmentProgressPerPolicyRequest,
  ) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.getAcknowledgmentProgressPerPolicy(params);

      if (!response) return;

      acknowledgementsPerPolicyData.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getAcknowledgementsPerUser = async (
    params: PoliciesApiGetUserAcknowledgmentProgressRequest,
  ) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.getUserAcknowledgmentProgress(params);

      if (!response) return;

      acknowledgementsPerUserData.value = response.data;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const exportPolicyVersionPdf = async (params: PoliciesApiExportPolicyVersionPdfRequest) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.exportPolicyVersionPdf(params, { responseType: 'blob' });

      if (!response) return;

      const data = response.data as unknown as Blob;
      downloadFile(data, `${currentPolicy.value?.name || 'policy'}.pdf`);
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const uploadPolicyVersionFile = async (params: PoliciesApiUploadPolicyVersionFileRequest) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      await policiesApi?.uploadPolicyVersionFile(params);
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getPolicyContentFile = async (params: PoliciesApiGetPolicyContentFileRequest) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.getPolicyContentFile(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getPolicyVersionContentFile = async (
    params: PoliciesApiGetPolicyVersionContentFileRequest,
  ) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.getPolicyVersionContentFile(params);

      if (!response) return;

      return response.data;
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const deletePolicyVersionContentFile = async (
    params: PoliciesApiDeletePolicyVersionContentFileRequest,
  ) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      await policiesApi?.deletePolicyVersionContentFile(params);
    } catch (error) {
      handleApiError(error as AxiosError);
    }
  };

  const getTemplateUsedByPolicy = async (params: PoliciesApiGetTemplateUsedByPolicyRequest) => {
    const policiesApi = await initApi(PoliciesApi);

    try {
      const response = await policiesApi?.getTemplateUsedByPolicy(params);

      if (!response) return;

      sourceTemplate.value = response.data;

      return response.data;
    } catch (error) {
      sourceTemplate.value = null;
      handleApiError(error as AxiosError);
    }
  };

  const resetCurrentPolicy = () => {
    currentPolicy.value = null;
    policyVersionsData.value = null;
    policyLogs.value = [];
    policyComments.value = [];
    sourceTemplate.value = null;
  };

  const resetSourceTemplate = () => {
    sourceTemplate.value = null;
  };

  const resetCurrentPolicyVersion = () => {
    currentPolicyVersion.value = null;
    policyVersionLogs.value = [];
    policyVersionComments.value = [];
  };

  const resetAcknowledgementsData = () => {
    policiesAcknowledgementsData.value = null;
    policiesAcknowledgementsExpandedData.value = null;
    acknowledgementsOverviewData.value = null;
    acknowledgementsPerPolicyData.value = null;
    acknowledgementsPerUserData.value = null;
    userAcknowledgementHistoryData.value = null;
  };

  return {
    // State
    policiesData,
    policiesExpandedData,
    currentPolicy,
    currentPolicyVersion,
    policyVersionsData,
    policyLogs,
    policyVersionLogs,
    policyComments,
    policyVersionComments,
    policiesAcknowledgementsData,
    policiesAcknowledgementsExpandedData,
    acknowledgementsOverviewData,
    acknowledgementsPerPolicyData,
    acknowledgementsPerUserData,
    userAcknowledgementHistoryData,
    sourceTemplate,

    // Actions
    queryPolicies,
    queryPoliciesExpanded,
    getPolicy,
    createPolicy,
    updatePolicyOwner,
    createPolicyVersion,
    getPolicyVersion,
    queryPolicyVersions,
    updatePolicyVersion,
    updatePolicyVersionStatus,
    updatePolicyStatus,
    exportPolicyVersionPdf,
    uploadPolicyVersionFile,
    getPolicyContentFile,
    getPolicyVersionContentFile,
    deletePolicyVersionContentFile,
    getPolicyLogs,
    getPolicyComments,
    getPolicyVersionLogs,
    postPolicyComment,
    getPolicyVersionComments,
    postPolicyVersionComment,
    acknowledgePolicy,
    queryPoliciesAcknowledgements,
    queryPoliciesAcknowledgementsExpanded,
    queryUserAcknowledgementHistory,
    getAcknowledgementsOverview,
    getAcknowledgementsPerPolicy,
    getAcknowledgementsPerUser,
    getTemplateUsedByPolicy,
    resetCurrentPolicy,
    resetCurrentPolicyVersion,
    resetSourceTemplate,
    resetAcknowledgementsData,
  };
});
